import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import { BarChart } from "@mui/x-charts/BarChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatHours,
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    formatUtcLocalShort,
    toNum,
} from "../../bids/analytics/format";
import { formatUtcDateTime } from "../../../common/dateTime";
import { AdCampaignStatus, AdCampaignStatusLabels } from "../../../models/adCampaign";
import {
    UeCampaignKpi,
    UeCampaignOverview,
    UeCampaignRoleSlice,
    UeUserContribution,
} from "../../../models/unitEconomics";
import { TermHint } from "../components/TermHint";
import { CampaignPromoTab } from "./CampaignPromoTab";
import {
    ueCampaignFunnelApi,
    ueCampaignMetaApi,
    ueCampaignMoneyApi,
    ueCampaignOverviewApi,
    ueCampaignQualityApi,
    ueCampaignRolesApi,
    ueCampaignTopUsersApi,
} from "../unitEconomicsApi";

const TAB_SLUGS = [
    "camp-overview",
    "camp-funnel",
    "camp-quality",
    "camp-roles",
    "camp-money",
    "camp-people",
    "camp-promo",
] as const;

type Props = {
    campaignId: number;
    onBack: () => void;
};

function statusColor(status: AdCampaignStatus): "neutral" | "success" | "warning" | "danger" {
    switch (status) {
        case AdCampaignStatus.Active:
            return "success";
        case AdCampaignStatus.Paused:
            return "warning";
        case AdCampaignStatus.Finished:
            return "danger";
        default:
            return "neutral";
    }
}

function formatRoas(roas: number | string | null | undefined): string {
    if (roas === null || roas === undefined || roas === "") {
        return "—";
    }
    const n = Number(roas);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return `${n.toFixed(2)}×`;
}

function formatRatio(v: number | string | null | undefined): string {
    if (v === null || v === undefined || v === "") {
        return "—";
    }
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return n.toFixed(2);
}

/** Удержание: «—», пока никто из пришедших не «дозрел» до N дней. */
function retentionValue(rate: number, eligible: number): string {
    return eligible > 0 ? formatRatePercent(rate) : "—";
}

function retentionHint(eligible: number): string {
    return eligible > 0 ? `из ${formatInt(eligible)} пришедших` : "ещё никто не дозрел";
}

function MetricGrid({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: 1,
            }}
        >
            {children}
        </Box>
    );
}

function ContributionTable({ users }: { users: UeUserContribution[] }) {
    return (
        <ScrollTable>
            <Table size="sm" hoverRow>
                <thead>
                    <tr>
                        <th>Пользователь</th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campOrders" />
                        </th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campFee" />
                        </th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campDepositProfit" />
                        </th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campWithdrawalProfit" />
                        </th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campPersonTotal" />
                        </th>
                        <th style={{ textAlign: "right" }}>
                            <TermHint term="campPersonPromo" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((u) => (
                        <tr key={String(u.userId)}>
                            <td>
                                <Link
                                    href={`/app/users/${u.userId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {u.userName || String(u.userId)}
                                </Link>
                            </td>
                            <td style={{ textAlign: "right" }}>{formatInt(u.orderCount)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.tradeProfit)}</td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.depositProfit)}</td>
                            <td style={{ textAlign: "right" }}>
                                {formatMoneyKopecks(u.withdrawalProfit)}
                            </td>
                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(u.totalProfit)}</td>
                            <td style={{ textAlign: "right" }}>
                                {u.promoCostKopecks ? `−${formatMoneyKopecks(u.promoCostKopecks)}` : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}

function RoleSliceCard({
    title,
    slice,
}: {
    title: React.ReactNode;
    slice: UeCampaignRoleSlice;
}) {
    return (
        <AnalyticsCard
            title={title}
            subtitle={`${formatInt(slice.count)} чел. · ${formatRatePercent(slice.share)} пришедших`}
            snapshotLabel="lifetime"
            howToRead={
                <List size="sm" marker="disc">
                    <ListItem>
                        <Typography level="body-sm">
                            <strong>Человек и доля</strong> — сколько пришедших в этой роли и какая это
                            часть всех пришедших: 12 из 100 — 12%.
                        </Typography>
                    </ListItem>
                    <ListItem>
                        <Typography level="body-sm">
                            <strong>Доход на человека</strong> — доход роли ÷ людей в ней: 6 000 ₽ ÷ 12 =
                            500 ₽.
                        </Typography>
                    </ListItem>
                    <ListItem>
                        <Typography level="body-sm">
                            Комиссия со сделки учитывается у покупателя, поэтому у тех, кто только продаёт,
                            доход может быть маленьким даже при большом обороте продаж.
                        </Typography>
                    </ListItem>
                </List>
            }
            watchFor={
                <WatchSignals
                    items={[
                        {
                            signal: <TermHint term="campRoleLtv" />,
                            ifGrows: "люди этой роли приносят больше на человека.",
                            ifFalls: "роль приносит меньше — смотрите оборот и комиссии.",
                        },
                        {
                            signal: (
                                <>
                                    <TermHint term="campRoleContribution" /> /{" "}
                                    <TermHint term="campGmvBuy" />
                                </>
                            ),
                            ifGrows: "роль даёт больше оборота и дохода.",
                            ifFalls: "роль приносит меньше — смотрите комиссии с покупок.",
                        },
                    ]}
                />
            }
        >
            <MetricGrid>
                <MetricHint label={<TermHint term="campRoleCount" />} value={formatInt(slice.count)} />
                <MetricHint label={<TermHint term="campRoleShare" />} value={formatRatePercent(slice.share)} />
                <MetricHint label={<TermHint term="campRoleLtv" />} value={formatMoneyKopecks(slice.ltv)} />
                <MetricHint
                    label={<TermHint term="campRoleContribution" />}
                    value={formatMoneyKopecks(slice.contribution)}
                    color="success"
                />
                <MetricHint label={<TermHint term="campGmvBuy" />} value={formatMoneyKopecks(slice.gmvBuy)} />
                <MetricHint label={<TermHint term="campGmvSell" />} value={formatMoneyKopecks(slice.gmvSell)} />
            </MetricGrid>
        </AnalyticsCard>
    );
}

function OverviewTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignOverviewApi(campaignId),
        isActive,
        [campaignId]
    );
    const d = state.data;
    const kpi = d?.summary;
    const series = d?.registeredSeries ?? [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {kpi && (
                <AnalyticsCard
                    title="Окупаемость кампании"
                    subtitle="Всё, что сделали пришедшие по кампании — от их прихода до конца кампании. Бюджет учитывается целиком."
                    snapshotLabel="lifetime"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Наведите на название метрики — там формула и пример. Ниже пример одной
                                    кампании: бюджет 10 000 ₽, пришли 100 человек, доход площадки 12 000 ₽,
                                    затраты на промо 1 000 ₽.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Сколько людей:</strong> пришли 100 → активировались 40 (40%) →
                                    со сделкой 30.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Сколько стоили:</strong> цена привлечения 10 000 ÷ 100 = 100 ₽,
                                    цена активации 10 000 ÷ 40 = 250 ₽.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Окупилась ли:</strong> окупаемость бюджета (12 000 − 1 000) ÷ 10 000
                                    = 1.10×, чистый результат 12 000 − 10 000 − 1 000 = 1 000 ₽.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    График — сколько человек пришло по кампании в каждый день.
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: (
                                        <>
                                            <TermHint term="campCac" /> /{" "}
                                            <TermHint term="campCostPerActivation" /> /{" "}
                                            <TermHint term="campCostPerTrade" />
                                        </>
                                    ),
                                    growsIsGood: false,
                                    ifGrows: "люди обходятся дороже — проверьте, активируются ли они.",
                                    ifFalls: "люди дешевле — проверьте, не упала ли доля активации.",
                                },
                                {
                                    signal: (
                                        <>
                                            <TermHint term="campRoas" /> / <TermHint term="campRomi" />
                                        </>
                                    ),
                                    ifGrows: "каждый рубль бюджета приносит больше дохода.",
                                    ifFalls: "доход от пришедших слабее относительно бюджета.",
                                },
                                {
                                    signal: <TermHint term="campLtvCac" />,
                                    ifGrows: "пришедшие окупают свою цену привлечения.",
                                    ifFalls: "человек приносит меньше, чем стоил, — смотрите активацию.",
                                },
                                {
                                    signal: <TermHint term="campNetResult" />,
                                    ifGrows: "после бюджета и промо кампания оставляет больше денег.",
                                    ifFalls: "бюджет и промо съедают доход — кампания ближе к минусу.",
                                },
                                {
                                    signal: <TermHint term="campSpendShare" />,
                                    growsIsGood: false,
                                    ifGrows: "реклама съедает большую часть дохода.",
                                    ifFalls: "бюджет легче относительно дохода.",
                                },
                                {
                                    signal: <TermHint term="campSpendShareOfGmv" />,
                                    growsIsGood: false,
                                    ifGrows: "реклама дорогая относительно оборота пришедших.",
                                    ifFalls: "бюджет легче относительно оборота.",
                                },
                            ]}
                        />
                    }
                >
                    <KpiOverview kpi={kpi} traded={d.traded} overview={d} />
                    {series.length > 0 && (
                        <Box sx={{ mt: 2, width: "100%", minHeight: 220 }}>
                            <BarChart
                                height={220}
                                series={[{ data: series.map((p) => toNum(p.count)), label: "Пришли" }]}
                                xAxis={[
                                    {
                                        data: series.map((p) => formatUtcLocalShort(p.bucketUtc)),
                                        scaleType: "band",
                                    },
                                ]}
                                margin={{ left: 40, right: 12, top: 20, bottom: 40 }}
                            />
                        </Box>
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

function KpiOverview({
    kpi,
    traded,
    overview,
}: {
    kpi: UeCampaignKpi;
    traded: number;
    overview: UeCampaignOverview;
}) {
    return (
        <MetricGrid>
            <MetricHint label={<TermHint term="campRegistered" />} value={formatInt(kpi.registered)} />
            <MetricHint label={<TermHint term="campActivated" />} value={formatInt(kpi.activated)} />
            <MetricHint label={<TermHint term="campTraded" />} value={formatInt(traded)} />
            <MetricHint
                label={<TermHint term="campActivationRate" />}
                value={formatRatePercent(kpi.activationRate)}
            />
            <MetricHint
                label={<TermHint term="campContribution" />}
                value={formatMoneyKopecks(kpi.totalContribution)}
                color="success"
            />
            <MetricHint
                label={<TermHint term="campPromoCost" />}
                value={kpi.promoCostKopecks ? formatMoneyKopecks(kpi.promoCostKopecks) : "—"}
            />
            <MetricHint
                label={<TermHint term="campNetResult" />}
                value={
                    kpi.netContribution != null ? formatMoneyKopecks(kpi.netContribution) : "—"
                }
                color={
                    kpi.netContribution == null
                        ? "neutral"
                        : kpi.netContribution >= 0
                          ? "success"
                          : "danger"
                }
            />
            <MetricHint label={<TermHint term="campArpu" />} value={formatMoneyKopecks(kpi.arpu)} />
            <MetricHint label={<TermHint term="campArppu" />} value={formatMoneyKopecks(kpi.arppu)} />
            <MetricHint
                label={<TermHint term="campLtvActivated" />}
                value={kpi.ltvActivated != null ? formatMoneyKopecks(kpi.ltvActivated) : "—"}
            />
            <MetricHint
                label={<TermHint term="campSpend" />}
                value={kpi.spendKopecks != null ? formatMoneyKopecks(kpi.spendKopecks) : "—"}
            />
            <MetricHint
                label={<TermHint term="campCac" />}
                value={kpi.cac != null ? formatMoneyKopecks(kpi.cac) : "—"}
            />
            <MetricHint
                label={<TermHint term="campCostPerActivation" />}
                value={kpi.costPerActivation != null ? formatMoneyKopecks(kpi.costPerActivation) : "—"}
            />
            <MetricHint
                label={<TermHint term="campCostPerTrade" />}
                value={kpi.costPerTrade != null ? formatMoneyKopecks(kpi.costPerTrade) : "—"}
            />
            <MetricHint
                label={<TermHint term="campRoas" />}
                value={formatRoas(kpi.roas)}
                color="primary"
            />
            <MetricHint label={<TermHint term="campRomi" />} value={formatRatio(kpi.romi)} />
            <MetricHint label={<TermHint term="campLtvCac" />} value={formatRatio(kpi.ltvCac)} />
            <MetricHint
                label={<TermHint term="campSpendShare" />}
                value={
                    kpi.spendShareOfContribution != null
                        ? formatRatePercent(kpi.spendShareOfContribution)
                        : "—"
                }
            />
            <MetricHint
                label={<TermHint term="campGmv" />}
                value={formatMoneyKopecks(overview.gmv)}
            />
            <MetricHint
                label={<TermHint term="campSpendShareOfGmv" />}
                value={
                    overview.spendShareOfGmv != null
                        ? formatRatePercent(overview.spendShareOfGmv)
                        : "—"
                }
            />
        </MetricGrid>
    );
}

function FunnelTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignFunnelApi(campaignId),
        isActive,
        [campaignId]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <>
                    <AnalyticsCard
                        title={<TermHint term="campFunnel" />}
                        subtitle="Пришли → пополнили баланс → купили; отдельно — продали."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        Каждый шаг считается от момента прихода человека до конца кампании.
                                        Процент под числом — доля от всех пришедших.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> пришли 100, пополнили 25 (25%), купили 20
                                        (20%), продали 15 (15%). Без депозита — 75%, без покупки — 80%.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Депозит → покупка</strong> — сколько пополнивших потом
                                        купили: из 25 пополнивших купили 15 — 60%. Кто купил без
                                        пополнения, сюда не входит.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campDeposited" />,
                                        ifGrows: "пришедшие охотнее пополняют баланс.",
                                        ifFalls: "люди застревают на первом пополнении — смотрите страницу и платежи.",
                                    },
                                    {
                                        signal: <TermHint term="campDepositToPurchase" />,
                                        ifGrows: "после пополнения люди начинают покупать.",
                                        ifFalls: "деньги лежат без дела — смотрите выбор скинов и цены.",
                                    },
                                    {
                                        signal: (
                                            <>
                                                <TermHint term="campWithoutDeposit" /> /{" "}
                                                <TermHint term="campWithoutPurchase" />
                                            </>
                                        ),
                                        growsIsGood: false,
                                        ifGrows: "больше пришедших ничего не делают.",
                                        ifFalls: "больше пришедших доходят до покупки.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campRegistered" />}
                                value={formatInt(d.funnel.registered)}
                            />
                            <MetricHint
                                label={<TermHint term="campDeposited" />}
                                value={formatInt(d.funnel.deposited)}
                                hint={formatRatePercent(d.funnel.depositRate)}
                                color="success"
                            />
                            <MetricHint
                                label={<TermHint term="campBought" />}
                                value={formatInt(d.funnel.traded)}
                                hint={formatRatePercent(d.funnel.tradeRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campSold" />}
                                value={formatInt(d.sold)}
                                hint={formatRatePercent(d.sellRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campWithoutDeposit" />}
                                value={formatRatePercent(d.withoutDepositRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campWithoutPurchase" />}
                                value={formatRatePercent(d.withoutTradeRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campDepositToPurchase" />}
                                value={
                                    d.funnel.deposited > 0
                                        ? formatRatePercent(d.funnel.depositToTradeRate)
                                        : "—"
                                }
                            />
                        </MetricGrid>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title="Скорость"
                        subtitle="Сколько часов проходит от прихода до первого пополнения и первой покупки."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <TermHint term="campTimeToDeposit" /> — от прихода до первого
                                        пополнения; <TermHint term="campTimeToPurchase" /> — до первой
                                        покупки. Под числом — сколько человек учтено.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <TermHint term="campMedian" /> — время типичного человека;{" "}
                                        <TermHint term="campP90" /> — время, за которое успели 9 из 10.
                                        Пример: 1, 2, 3, 10 и 100 часов — медиана 3 часа, 90% уложились
                                        почти в 100.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campMedian" />,
                                        growsIsGood: false,
                                        ifGrows: "типичному человеку нужно больше времени на первый шаг.",
                                        ifFalls: "быстрее доходят до пополнения и покупки.",
                                    },
                                    {
                                        signal: <TermHint term="campP90" />,
                                        growsIsGood: false,
                                        ifGrows: "часть пришедших застревает надолго.",
                                        ifFalls: "даже медленные доходят быстрее.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={
                                    <>
                                        <TermHint term="campMedian" />: <TermHint term="campTimeToDeposit" />
                                    </>
                                }
                                value={formatHours(
                                    d.timeToValue.medianHoursToDeposit,
                                    d.timeToValue.usersWithDeposit
                                )}
                                hint={`${formatInt(d.timeToValue.usersWithDeposit)} чел.`}
                            />
                            <MetricHint
                                label={
                                    <>
                                        <TermHint term="campP90" />: <TermHint term="campTimeToDeposit" />
                                    </>
                                }
                                value={formatHours(
                                    d.timeToValue.p90HoursToDeposit,
                                    d.timeToValue.usersWithDeposit
                                )}
                            />
                            <MetricHint
                                label={
                                    <>
                                        <TermHint term="campMedian" />: <TermHint term="campTimeToPurchase" />
                                    </>
                                }
                                value={formatHours(
                                    d.timeToValue.medianHoursToTrade,
                                    d.timeToValue.usersWithTrade
                                )}
                                hint={`${formatInt(d.timeToValue.usersWithTrade)} чел.`}
                            />
                            <MetricHint
                                label={
                                    <>
                                        <TermHint term="campP90" />: <TermHint term="campTimeToPurchase" />
                                    </>
                                }
                                value={formatHours(
                                    d.timeToValue.p90HoursToTrade,
                                    d.timeToValue.usersWithTrade
                                )}
                            />
                        </MetricGrid>
                    </AnalyticsCard>
                </>
            )}
        </Box>
    );
}

function QualityTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignQualityApi(campaignId),
        isActive,
        [campaignId]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <>
                    <AnalyticsCard
                        title="Повторы и покупки"
                        subtitle="Возвращаются ли пришедшие за второй покупкой и вторым пополнением."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Одна покупка</strong> и <strong>Повторная покупка</strong>{" "}
                                        считаются от всех пришедших: 12 из 100 купили один раз (12%), 8 из
                                        100 — два и больше (8%).
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Повторный депозит</strong> — среди тех, кто уже пополнял:
                                        из 25 пополнивших ещё раз пополнили 10 — 40%. Под числом — «10 / 25».
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        Возвраты покупкой не считаются: покупатель скин не получил.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campSingleTrade" />,
                                        growsIsGood: false,
                                        ifGrows: "больше людей купили один раз и не вернулись.",
                                        ifFalls: "больше людей делают вторую покупку.",
                                    },
                                    {
                                        signal: <TermHint term="campRepeatPurchase" />,
                                        ifGrows: "больше пришедших возвращаются за второй покупкой.",
                                        ifFalls: "покупки становятся разовыми.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campSingleTrade" />}
                                value={formatRatePercent(d.singleTradeRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campRepeatDeposit" />}
                                value={formatRatePercent(d.repeatRate.repeatDepositRate)}
                                hint={`${formatInt(d.repeatRate.usersWithRepeatDeposit)} / ${formatInt(d.repeatRate.usersWithDeposit)}`}
                            />
                            <MetricHint
                                label={<TermHint term="campRepeatPurchase" />}
                                value={formatRatePercent(d.repeatTradeRate)}
                                hint={`${formatInt(d.repeatRate.usersWithRepeatOrder)} / ${formatInt(d.retention.registered)} · среди купивших ${formatRatePercent(d.repeatRate.repeatOrderRate)}`}
                            />
                            <MetricHint
                                label={<TermHint term="campPurchasesPerBuyer" />}
                                value={toNum(d.repeatRate.avgOrdersPerActiveBuyer).toFixed(2)}
                            />
                        </MetricGrid>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title="Кто приносит доход"
                        subtitle="Доля пришедших, на которых площадка заработала, ничего не заработала или потеряла."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        Смотрим на доход площадки с каждого пришедшего: комиссии с его покупок
                                        плюс наш доход с его пополнений и выводов.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> из 100 пришедших 25 принесли доход, 73 — ноль
                                        (ничего не сделали или купили без комиссии), 2 — в минусе (вывод обошёлся
                                        дороже, чем мы на нём заработали).
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campPositive" />,
                                        ifGrows: "больше пришедших реально зарабатывают площадке.",
                                        ifFalls: "меньше людей приносят доход.",
                                    },
                                    {
                                        signal: <TermHint term="campZero" />,
                                        growsIsGood: false,
                                        ifGrows: "больше пришедших ничего не приносят.",
                                        ifFalls: "больше людей начинают приносить доход или убыток.",
                                    },
                                    {
                                        signal: <TermHint term="campNegative" />,
                                        growsIsGood: false,
                                        ifGrows: "растёт доля убыточных — смотрите выводы и доход с пополнений.",
                                        ifFalls: "меньше людей в минусе.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campPositive" />}
                                value={formatRatePercent(d.positiveContributionRate)}
                                color="success"
                            />
                            <MetricHint
                                label={<TermHint term="campZero" />}
                                value={formatRatePercent(d.zeroContributionRate)}
                            />
                            <MetricHint
                                label={<TermHint term="campNegative" />}
                                value={formatRatePercent(d.negativeContributionRate)}
                                color="danger"
                            />
                        </MetricGrid>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title={<TermHint term="campRetention" />}
                        subtitle="Заходили ли пришедшие на площадку через 1, 7 и 30 дней после прихода."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        Считаем только тех, кто пришёл достаточно давно: в «через неделю» —
                                        пришедших неделю назад и раньше. Под числом — из скольких считали.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> неделю назад и раньше пришли 60 человек, через
                                        7 и больше дней заходили 18 — 30%. Вчерашние 40 человек в расчёт не
                                        попадают.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: (
                                            <>
                                                <TermHint term="campD1" /> / <TermHint term="campD7" /> /{" "}
                                                <TermHint term="campD30" />
                                            </>
                                        ),
                                        ifGrows: "пришедшие возвращаются на площадку.",
                                        ifFalls: "заходят один раз и пропадают.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campD1" />}
                                value={retentionValue(d.retention.d1, d.retention.d1Eligible)}
                                hint={retentionHint(d.retention.d1Eligible)}
                            />
                            <MetricHint
                                label={<TermHint term="campD7" />}
                                value={retentionValue(d.retention.d7, d.retention.d7Eligible)}
                                hint={retentionHint(d.retention.d7Eligible)}
                            />
                            <MetricHint
                                label={<TermHint term="campD30" />}
                                value={retentionValue(d.retention.d30, d.retention.d30Eligible)}
                                hint={retentionHint(d.retention.d30Eligible)}
                            />
                        </MetricGrid>
                    </AnalyticsCard>
                </>
            )}
        </Box>
    );
}

function RolesTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignRolesApi(campaignId),
        isActive,
        [campaignId]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <>
                    <AnalyticsCard
                        title="Роли пришедших"
                        subtitle={`Пришли ${formatInt(d.registered)} чел. Кто из них покупает, кто продаёт.`}
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Только покупают</strong>, <strong>только продают</strong> или{" "}
                                        <strong>покупают и продают</strong> — по сделкам после прихода. Те, у
                                        кого сделок нет, ни в одну роль не входят, поэтому доли не дают в сумме
                                        100%.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> из 100 пришедших 12 только покупают, 10 только
                                        продают, 8 делают и то и другое, у 70 сделок нет.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        Оборот покупок — у тех, кто покупает (только покупают + покупают и
                                        продают); оборот продаж — у тех, кто продаёт. Кто делает и то и
                                        другое, попадает в оба. Возврат — продажа, но не покупка.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campHybrid" />,
                                        ifGrows: "больше людей и покупают, и продают — им интересна площадка.",
                                        ifFalls: "пришедшие делают что-то одно.",
                                    },
                                    {
                                        signal: <TermHint term="campSellerOnly" />,
                                        growsIsGood: false,
                                        ifGrows: "много продавцов без покупок — комиссии с них почти нет.",
                                        ifFalls: "больше пришедших покупают.",
                                    },
                                    {
                                        signal: (
                                            <>
                                                <TermHint term="campGmvBuy" /> /{" "}
                                                <TermHint term="campGmvSell" />
                                            </>
                                        ),
                                        ifGrows: "пришедшие больше покупают или продают.",
                                        ifFalls: "оборот падает — смотрите, сколько людей в роли.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint label={<TermHint term="campRegistered" />} value={formatInt(d.registered)} />
                            <MetricHint
                                label={<TermHint term="campBuyerOnly" />}
                                value={formatInt(d.buyerOnly.count)}
                                hint={formatRatePercent(d.buyerOnly.share)}
                            />
                            <MetricHint
                                label={<TermHint term="campSellerOnly" />}
                                value={formatInt(d.sellerOnly.count)}
                                hint={formatRatePercent(d.sellerOnly.share)}
                            />
                            <MetricHint
                                label={<TermHint term="campHybrid" />}
                                value={formatInt(d.hybrid.count)}
                                hint={formatRatePercent(d.hybrid.share)}
                            />
                            <MetricHint
                                label={<TermHint term="campGmvBuy" />}
                                value={formatMoneyKopecks(d.gmvBuy)}
                            />
                            <MetricHint
                                label={<TermHint term="campGmvSell" />}
                                value={formatMoneyKopecks(d.gmvSell)}
                            />
                        </MetricGrid>
                    </AnalyticsCard>
                    <RoleSliceCard title="Только покупают" slice={d.buyerOnly} />
                    <RoleSliceCard title="Только продают" slice={d.sellerOnly} />
                    <RoleSliceCard title="Покупают и продают" slice={d.hybrid} />
                </>
            )}
        </Box>
    );
}

function MoneyTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignMoneyApi(campaignId),
        isActive,
        [campaignId]
    );
    const d = state.data;
    const series = d?.paybackSeries ?? [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <>
                    <AnalyticsCard
                        title="Пополнения и выводы"
                        subtitle="Полные суммы пополнений и выводов пришедших — не наш доход с них."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Первые депозиты</strong> — только первое пополнение каждого
                                        человека после прихода: 25 человек пополнили впервые на 25 000 ₽,
                                        в среднем 1 000 ₽.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Объёмы</strong> — все пополнения и выводы за срок кампании:
                                        пополнили 40 000 ₽, вывели 15 000 ₽ — чистый приток 25 000 ₽.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campFirstDepositAvg" />,
                                        ifGrows: "первые пополнения крупнее — пришедшие готовы тратить больше.",
                                        ifFalls: "первые пополнения мелкие.",
                                    },
                                    {
                                        signal: <TermHint term="campNetFlow" />,
                                        ifGrows: "пришедшие оставляют деньги на площадке.",
                                        ifFalls: "выводят больше, чем пополняют, — смотрите выводы.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campFirstDeposits" />}
                                value={formatInt(d.firstDepositCount)}
                            />
                            <MetricHint
                                label={<TermHint term="campFirstDepositSum" />}
                                value={formatMoneyKopecks(d.firstDepositSumKopecks)}
                            />
                            <MetricHint
                                label={<TermHint term="campFirstDepositAvg" />}
                                value={formatMoneyKopecks(d.firstDepositAvgKopecks)}
                            />
                            <MetricHint
                                label={<TermHint term="campDepositVolume" />}
                                value={formatMoneyKopecks(d.totalDepositVolumeKopecks)}
                            />
                            <MetricHint
                                label={<TermHint term="campWithdrawalVolume" />}
                                value={formatMoneyKopecks(d.totalWithdrawalVolumeKopecks)}
                            />
                            <MetricHint
                                label={<TermHint term="campNetFlow" />}
                                value={formatMoneyKopecks(d.netDepositFlowKopecks)}
                                color="primary"
                            />
                        </MetricGrid>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title={<TermHint term="campPayback" />}
                        subtitle="Через сколько дней после создания кампании доход за вычетом промо покрыл бюджет."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        Со дня создания кампании каждый день добавляем доход площадки и
                                        вычитаем затраты на промо. Сколько дней прошло до дня, когда сумма
                                        дошла до бюджета, — окупаемость; день создания — нулевой.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> бюджет 10 000 ₽; через 12 дней после создания
                                        накопили 10 300 ₽ — окупаемость 12 дней. «—» — бюджет не указан или ещё
                                        не окупились. График — накопленный результат по дням.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                        watchFor={
                            <WatchSignals
                                items={[
                                    {
                                        signal: <TermHint term="campPayback" />,
                                        growsIsGood: false,
                                        ifGrows: "дольше ждать, пока кампания отобьёт бюджет.",
                                        ifFalls: "кампания отбивается быстрее.",
                                    },
                                    {
                                        signal: <TermHint term="campCumulative" />,
                                        ifGrows: "ближе к бюджету или уже выше.",
                                        ifFalls: "пришедшие приносят меньше — окупаемость отодвигается.",
                                    },
                                ]}
                            />
                        }
                    >
                        <MetricGrid>
                            <MetricHint
                                label={<TermHint term="campPayback" />}
                                value={
                                    d.paybackDays != null ? `${formatInt(d.paybackDays)} д` : "—"
                                }
                            />
                        </MetricGrid>
                        {series.length > 0 && (
                            <Box sx={{ mt: 2, width: "100%", minHeight: 220 }}>
                                <BarChart
                                    height={220}
                                    series={[
                                        {
                                            data: series.map((p) => toNum(p.value)),
                                            label: "Накопленный результат",
                                        },
                                    ]}
                                    xAxis={[
                                        {
                                            data: series.map((p) => formatUtcLocalShort(p.bucketUtc)),
                                            scaleType: "band",
                                        },
                                    ]}
                                    margin={{ left: 48, right: 12, top: 20, bottom: 40 }}
                                />
                            </Box>
                        )}
                    </AnalyticsCard>
                </>
            )}
        </Box>
    );
}

function PeopleTab({ campaignId, isActive }: { campaignId: number; isActive: boolean }) {
    const [state, refresh] = useAsyncBlock(
        () => ueCampaignTopUsersApi(campaignId, 20),
        isActive,
        [campaignId]
    );
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && (
                <AnalyticsCard
                    title="Пришедшие по кампании"
                    subtitle={`Показано ${formatInt(d.topUsers.length)} из ${formatInt(d.cohortSize)} · доход площадки со всех пришедших ${formatMoneyKopecks(d.totalContribution)} · затраты на промо ${formatMoneyKopecks(d.promoCostKopecks)} — как в Обзоре.`}
                    snapshotLabel="lifetime"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    Все пришедшие по кампании (до 20 человек), сначала те, кто принёс больше
                                    дохода. Люди с нулевым доходом тоже в списке: например, купил скин за
                                    100 ₽ без комиссии площадки и без пополнения — доход 0, но покупка видна.
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <strong>Итого</strong> = комиссия + с пополнений + с выводов: 120 + 20 +
                                    20 = 160 ₽. <strong>Промо</strong> — во сколько обошлись его
                                    промокоды: скидка 1% с покупки за 100 ₽ — минус 1 ₽.
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: "Доход первых в списке",
                                    growsIsGood: false,
                                    ifGrows: "доход держится на нескольких крупных людях — рискованно.",
                                    ifFalls: "доход распределён по пришедшим шире.",
                                },
                                {
                                    signal: <TermHint term="campWithdrawalProfit" />,
                                    growsIsGood: false,
                                    ifGrows: "выводы обходятся дороже — минус съедает комиссии.",
                                    ifFalls: "меньше потерь на выводах.",
                                },
                            ]}
                        />
                    }
                >
                    {d.topUsers.length === 0 ? (
                        <EmptyState hint="По кампании пока никто не пришёл." />
                    ) : (
                        <ContributionTable users={d.topUsers} />
                    )}
                </AnalyticsCard>
            )}
        </Box>
    );
}

const tabListSx = {
    justifyContent: "left",
    [`&& .${tabClasses.root}`]: {
        fontWeight: "600",
        flex: "initial",
        color: "text.tertiary",
        [`&.${tabClasses.selected}`]: {
            bgcolor: "transparent",
            color: "text.primary",
            "&::after": {
                height: "2px",
                bgcolor: "primary.500",
            },
        },
    },
} as const;

export function CampaignDetail({ campaignId, onBack }: Props) {
    const [tab, setTab] = React.useState(0);

    const [metaState, refreshMeta] = useAsyncBlock(
        () => ueCampaignMetaApi(campaignId),
        true,
        [campaignId]
    );
    const meta = metaState.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Button size="sm" variant="plain" onClick={onBack}>
                    ← К списку кампаний
                </Button>
                <Button
                    size="sm"
                    variant="outlined"
                    loading={metaState.loading}
                    onClick={refreshMeta}
                >
                    Обновить шапку
                </Button>
            </Box>

            {metaState.loading && !meta && <BlockLoading />}
            {metaState.error && <BlockError message={metaState.error} onRetry={refreshMeta} />}

            {meta && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Typography level="h3">{meta.name}</Typography>
                        <Chip size="sm" variant="soft" color={statusColor(meta.status)}>
                            {AdCampaignStatusLabels[meta.status]}
                        </Chip>
                        <Chip size="sm" variant="soft">
                            {meta.channel}
                        </Chip>
                        <Chip size="sm" variant="soft" color="primary">
                            Весь срок кампании
                        </Chip>
                    </Box>
                    <Typography level="body-sm" color="neutral">
                        Создана {formatUtcDateTime(meta.createdAt)}
                        {" · "}
                        Старт {formatUtcDateTime(meta.startAt)}
                        {meta.endAt ? ` · Конец ${formatUtcDateTime(meta.endAt)}` : ""}
                        {" · "}
                        Бюджет{" "}
                        {meta.spendKopecks != null ? formatMoneyKopecks(meta.spendKopecks) : "—"}
                    </Typography>
                </Box>
            )}

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v as number)}
                sx={{ bgcolor: "transparent" }}
            >
                <TabList size="sm" sx={tabListSx}>
                    <Tab value={0}>Обзор</Tab>
                    <Tab value={1}>Воронка</Tab>
                    <Tab value={2}>Качество</Tab>
                    <Tab value={3}>Роли</Tab>
                    <Tab value={4}>Деньги</Tab>
                    <Tab value={5}>Люди</Tab>
                    <Tab value={6}>Промокод</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0, pt: 1.5 }}>
                    <OverviewTab campaignId={campaignId} isActive={tab === 0} />
                </TabPanel>
                <TabPanel value={1} sx={{ p: 0, pt: 1.5 }}>
                    <FunnelTab campaignId={campaignId} isActive={tab === 1} />
                </TabPanel>
                <TabPanel value={2} sx={{ p: 0, pt: 1.5 }}>
                    <QualityTab campaignId={campaignId} isActive={tab === 2} />
                </TabPanel>
                <TabPanel value={3} sx={{ p: 0, pt: 1.5 }}>
                    <RolesTab campaignId={campaignId} isActive={tab === 3} />
                </TabPanel>
                <TabPanel value={4} sx={{ p: 0, pt: 1.5 }}>
                    <MoneyTab campaignId={campaignId} isActive={tab === 4} />
                </TabPanel>
                <TabPanel value={5} sx={{ p: 0, pt: 1.5 }}>
                    <PeopleTab campaignId={campaignId} isActive={tab === 5} />
                </TabPanel>
                <TabPanel value={6} sx={{ p: 0, pt: 1.5 }}>
                    <CampaignPromoTab campaignId={campaignId} isActive={tab === 6} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}

// silence unused slug export for future hash wiring
void TAB_SLUGS;
